import React, { useCallback, useMemo } from 'react';
import { Button, IconButton, Divider, Flex, Text, Counter } from '@vibe/core';
import { CloseSmall, Add } from '@vibe/icons';
import { useFilterStore } from '../../stores/filterStore';
import AssigneeTeamToggle, { type GroupByMode } from './AssigneeTeamToggle';
import ResourceSelectionDropdown from './ResourceSelectionDropdown';
import FilterDropdown from './FilterDropdown';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilterBarProps {
  /** Selectable assignees for filtering */
  assigneeOptions: Array<{ id: number; name: string; avatarUrl?: string }>;
  /** Selectable teams for filtering (with optional member names) */
  teamOptions: Array<{ id: number; name: string; memberNames?: string[] }>;
  /** Current grouping mode for the scheduler rows */
  groupByMode: GroupByMode;
  /** Called when the user toggles between Users / Teams / Team with Users */
  onGroupByModeChange: (mode: GroupByMode) => void;
  /** Board options for workspace multi-board filtering (passed to FilterDropdown) */
  boardOptions?: Array<{ id: number; name: string }>;
  /** Whether status column is mapped (enables completion filter in FilterDropdown) */
  hasStatusColumn?: boolean;
  onToggleSearchPanel?: () => void;
  isSearchPanelOpen?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Filter bar rendered within the TopBar area of the scheduler board.
 *
 * Header row (always visible):
 * - Resource Type dropdown (Users / Teams / Team with Users)
 * - Resource Selection dropdown (pick specific teams or users)
 * - Leaves / Holidays visibility toggles
 * - Filter button (opens dropdown with Assignees, Teams, Boards, Status tabs)
 * - Active filter chips + Clear all
 */
const FilterBar: React.FC<FilterBarProps> = ({
  assigneeOptions,
  teamOptions,
  groupByMode,
  onGroupByModeChange,
  boardOptions,
  hasStatusColumn,
  onToggleSearchPanel,
  isSearchPanelOpen,
}) => {
  const {
    assignees: selectedAssignees,
    teams: selectedTeams,
    selectedBoardIds,
    completionFilter,
    showLeaves,
    showHolidays,
    clearFilters,
    setAssignees,
    setTeams,
    setSelectedBoardIds,
    setCompletionFilter,
    setShowLeaves,
    setShowHolidays,
  } = useFilterStore();

  const completionFilterActive = completionFilter !== 'all' ? 1 : 0;
  const totalActiveFilters = selectedBoardIds.length + completionFilterActive;

  // ── Chip removal handlers ─────────────────────────────────────────────────

  const removeAssignee = useCallback(
    (id: number) => {
      setAssignees(selectedAssignees.filter((a) => a !== id));
    },
    [selectedAssignees, setAssignees],
  );

  const removeTeam = useCallback(
    (id: number) => {
      setTeams(selectedTeams.filter((t) => t !== id));
    },
    [selectedTeams, setTeams],
  );

  const removeBoard = useCallback(
    (id: number) => {
      setSelectedBoardIds(selectedBoardIds.filter((b) => b !== id));
    },
    [selectedBoardIds, setSelectedBoardIds],
  );

  // ── Resolve names for active filter chips ─────────────────────────────────

  const assigneeChips = useMemo(() => {
    const lookup = new Map(assigneeOptions.map((a) => [a.id, a.name]));
    return selectedAssignees.map((id) => ({
      id,
      label: lookup.get(id) ?? `User ${id}`,
    }));
  }, [selectedAssignees, assigneeOptions]);

  const teamChips = useMemo(() => {
    const lookup = new Map(teamOptions.map((t) => [t.id, t.name]));
    return selectedTeams.map((id) => ({
      id,
      label: lookup.get(id) ?? `Team ${id}`,
    }));
  }, [selectedTeams, teamOptions]);

  const boardChips = useMemo(() => {
    if (!boardOptions) return [];
    const lookup = new Map(boardOptions.map((b) => [b.id, b.name]));
    return selectedBoardIds.map((id) => ({
      id,
      label: lookup.get(id) ?? `Board ${id}`,
    }));
  }, [selectedBoardIds, boardOptions]);

  return (
    <Flex
      align={Flex.align.CENTER}
      gap={Flex.gaps.SMALL}
      className="rp-filter-bar"
    >
      {/* Resource Type dropdown (Users / Teams / Team with Users) */}
      <AssigneeTeamToggle value={groupByMode} onChange={onGroupByModeChange} />

      {/* Resource Selection dropdown (pick specific teams or users) */}
      <ResourceSelectionDropdown
        groupByMode={groupByMode}
        teamOptions={teamOptions}
        userOptions={assigneeOptions}
        selectedTeamIds={selectedTeams}
        selectedUserIds={selectedAssignees}
        onTeamSelectionChange={setTeams}
        onUserSelectionChange={setAssignees}
      />

      {/* Show/hide leaves & holidays toggle buttons */}
      <Divider direction={Divider.directions.VERTICAL} />
      <div className="rp-filter-bar__toggle-group">
        <button
          type="button"
          className={`rp-filter-bar__toggle-btn${showLeaves ? ' rp-filter-bar__toggle-btn--active' : ''}`}
          onClick={() => setShowLeaves(!showLeaves)}
          aria-pressed={showLeaves}
        >
          Leaves
        </button>
        <div className="rp-filter-bar__toggle-separator" />
        <button
          type="button"
          className={`rp-filter-bar__toggle-btn${showHolidays ? ' rp-filter-bar__toggle-btn--active' : ''}`}
          onClick={() => setShowHolidays(!showHolidays)}
          aria-pressed={showHolidays}
        >
          Holidays
        </button>
      </div>

      {/* Vertical visual separator */}
      <Divider direction={Divider.directions.VERTICAL} />

      {/* Filter dropdown (Assignees, Teams + optional Boards & Status) */}
      <FilterDropdown
        boardOptions={boardOptions}
        hasStatusColumn={hasStatusColumn}
      />

      {/* Clear all filters */}
      {totalActiveFilters > 0 && (
        <Button
          size={Button.sizes.XS}
          kind={Button.kinds.TERTIARY}
          leftIcon={CloseSmall}
          onClick={() => { setSelectedBoardIds([]); setCompletionFilter('all'); }}
          className="rp-filter-bar__clear-btn"
        >
          <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER}>
            <span>Clear</span>
            <Counter
              count={totalActiveFilters}
              size={Counter.sizes.SMALL}
              color={Counter.colors.NEGATIVE}
            />
          </Flex>
        </Button>
      )}

      {/* Spacer to push Plan Item to the right */}
      <div style={{ flex: 1 }} />

      {/* Item search panel toggle */}
      {onToggleSearchPanel && (
        <Button
          size={Button.sizes.SMALL}
          kind={isSearchPanelOpen ? Button.kinds.PRIMARY : Button.kinds.SECONDARY}
          leftIcon={Add}
          onClick={onToggleSearchPanel}
        >
          Plan Item
        </Button>
      )}
    </Flex>
  );
};

// ─── Filter Chip ──────────────────────────────────────────────────────────────

interface FilterChipProps {
  label: string;
  onRemove: () => void;
}

/**
 * Small removable chip representing a single active filter value.
 */
const FilterChip: React.FC<FilterChipProps> = ({ label, onRemove }) => {
  return (
    <span className="rp-filter-chip">
      <Text type={Text.types.TEXT2} className="rp-filter-chip__label">
        {label}
      </Text>
      <button
        type="button"
        className="rp-filter-chip__remove"
        onClick={onRemove}
        aria-label={`Remove filter: ${label}`}
      >
        &times;
      </button>
    </span>
  );
};

export default FilterBar;
